import axios from "axios";
import { getAccessToken } from "../../../shared/utils/axios";
import {
  calculatePlotAreaHa,
  calculateTotalAmount,
} from "../../../shared/utils/areaCalculation";
import {
  getSeedPolygonCoordinates,
  type SeedGeometryLike,
} from "../utils/seedGeometry";

export interface SeedCellAssignment {
  plot_group_id: number;
  plot_cell_id: number;
  rate: number;
  quantity?: number | null;
  color?: string;
}

export interface SeedPlotGroupTotal {
  plot_group_id: number;
  value: number;
  measure_unit: string;
}

export interface SavedSeedProductAssignment {
  product_id: string | null;
  variety: string;
  measure_unit: string;
  is_standard: boolean;
  assignments: SeedCellAssignment[];
  plot_group_totals?: SeedPlotGroupTotal[];
}

export interface SeedProductAssignment
  extends Omit<SavedSeedProductAssignment, "product_id"> {
  product_id: string;
}

export interface SeedAssignmentsResponse {
  seed_product_assignments: SavedSeedProductAssignment[] | null;
}

export interface LegacySeedEntry {
  product_id?: string | null;
  variety?: string | null;
  rate?: number | null;
  is_standard?: boolean;
  plot_id?: number | null;
  plot_cell_id?: number | null;
  color?: string | null;
  quantity?: number | null;
  measure_unit?: string | null;
  [key: string]: unknown;
}

export interface SeedPlot {
  plot?: {
    id?: number;
    name?: string;
    plot_id?: number | null;
    rows?: number;
    columns?: number;
    column_size?: number;
    row_size?: number;
    plotWidth?: number;
    plotHeight?: number;
    stripArea?: number;
    stripLength?: number;
    stripWidth?: number;
    is_strip?: boolean;
    isStripPlot?: boolean;
    geojson?: {
      type?: string;
      geometry?: SeedGeometryLike;
    };
  };
  replicants?: unknown[] | Record<string, unknown> | null;
  properties?: {
    seeds?: {
      measure_unit?: string | null;
      rates_and_dosages?: LegacySeedEntry[] | null;
      calculated_totals?: unknown;
      [key: string]: unknown;
    } | null;
    [key: string]: unknown;
  };
}

export interface SeedAutoAssignmentResponse {
  trial_plots: SeedPlot[];
}

export interface SeedStandard {
  standard_seed_product_id: string;
  standard_product: string;
  standard_seed_rate: number;
}

export interface SeedTrial {
  id: string;
  protocol_id?: string;
  trial_plots?: SeedPlot[];
  strip_design?: {
    is_strip?: boolean;
  } | null;
  plot_property_assignments?: SeedAssignmentsResponse | null;
  standard_field_properties?: {
    standard_seed_product_id?: string | null;
    standard_product?: string;
    standard_seed_rate?: number | null;
    [key: string]: unknown;
  };
  is_exportable?: boolean;
  missing_fields?: string[];
  execution_status?: string;
  [key: string]: unknown;
}

export interface SeedProduct {
  id: string;
  name: string;
  color: string;
  range?: {
    min: number;
    max: number;
  };
  options: number[];
}

export interface SeedAssignmentRow {
  id: string;
  group: string;
  plot: number;
  plotGroupId: number;
  plotCellId: number;
  productId: string;
  variety: string;
  seedRate: number | null;
  isStandard: boolean;
  color: string;
  legacy: boolean;
}

export interface AssignmentScope {
  orgId: string;
  workspaceId: string;
}

export const nonEmptyString = (
  value: string | null | undefined
): string | undefined => {
  if (value === undefined || value === null || value.trim() === "") {
    return undefined;
  }

  return value;
};

const client = axios.create({
  baseURL: process.env.REACT_APP_API_URL ?? "",
});

const headersFor = (scope: AssignmentScope): Record<string, string> => ({
  Authorization: `Bearer ${getAccessToken()}`,
  "Cache-Control": "no-cache",
  "org-id": scope.orgId,
  "workspace-id": scope.workspaceId,
});

export const seedTrialQueryKey = (
  trialId: string,
  scope: AssignmentScope
): readonly string[] => [
  "seedAssignmentsTrial",
  scope.orgId,
  scope.workspaceId,
  trialId,
];

export const seedAutoAssignmentQueryKey = (
  trialId: string,
  scope: AssignmentScope
): readonly string[] => [
  "seedAutoAssignments",
  scope.orgId,
  scope.workspaceId,
  trialId,
  "seed",
];

export const normalizeSeedUnit = (unit: string): string =>
  unit.toLowerCase().replace(/²/g, "2").replace(/\s/g, "");

export const convertSeedRate = (
  rate: number,
  fromUnit: string,
  toUnit: string
): number => {
  const from = normalizeSeedUnit(fromUnit);
  const to = normalizeSeedUnit(toUnit);

  if (from === to) {
    return rate;
  }

  if (
    (from === "seeds/ha" && to === "seeds/m2") ||
    (from === "kg/ha" && to === "kg/m2")
  ) {
    return rate / 10000;
  }

  if (
    (from === "seeds/m2" && to === "seeds/ha") ||
    (from === "kg/m2" && to === "kg/ha")
  ) {
    return rate * 10000;
  }

  throw new Error(
    `Unsupported seed-unit conversion: ${fromUnit} to ${toUnit}.`
  );
};

const groupLabel = (index: number): string => {
  let value = index + 1;
  let result = "";

  while (value > 0) {
    value -= 1;
    result = String.fromCharCode(65 + (value % 26)) + result;
    value = Math.floor(value / 26);
  }

  return result;
};

const cellKey = (groupIndex: number, cellIndex: number): string =>
  `${groupIndex}:${cellIndex}`;

const getGridCellCount = (group: SeedPlot, groupIndex: number): number => {
  const plot = group.plot;

  if (plot === undefined) {
    throw new Error(`Plot group ${groupIndex + 1} is missing its design.`);
  }

  if (plot.is_strip === true || plot.isStripPlot === true) {
    throw new Error("Strip plots must use the separate stripe workflow.");
  }

  const rows = plot.rows ?? 0;
  const columns = plot.columns ?? 0;

  if (
    !Number.isInteger(rows) ||
    !Number.isInteger(columns) ||
    rows <= 0 ||
    columns <= 0
  ) {
    throw new Error(
      `Plot group ${groupIndex + 1} has invalid grid dimensions.`
    );
  }

  const coordinates = getSeedPolygonCoordinates(plot.geojson?.geometry);
  const expected = rows * columns;

  if (coordinates.length !== expected) {
    throw new Error(
      `Plot group ${groupIndex + 1} contains ` +
        `${coordinates.length} stored cells, but its dimensions require ` +
        `${expected} cells.`
    );
  }

  return expected;
};

/**
 * Keep backend plot metadata separate from cell identity:
 *
 * - plot.plot_id may be null.
 * - entry.plot_id is the legacy one-based cell number.
 * - entry.plot_cell_id is the zero-based cell number.
 *
 * Array position is safe only for a complete legacy cell array.
 */
const getLegacyCellIndex = (
  entry: LegacySeedEntry,
  entryIndex: number,
  entryCount: number,
  cellCount: number,
  groupIndex: number
): number => {
  let cellIndex: number;

  if (entry.plot_cell_id !== undefined && entry.plot_cell_id !== null) {
    cellIndex = entry.plot_cell_id;
  } else if (entry.plot_id !== undefined && entry.plot_id !== null) {
    cellIndex = entry.plot_id - 1;
  } else {
    if (entryCount !== cellCount) {
      throw new Error(
        `Plot group ${groupIndex + 1} has partial legacy seed data ` +
          "without explicit cell references."
      );
    }

    cellIndex = entryIndex;
  }

  if (!Number.isInteger(cellIndex) || cellIndex < 0 || cellIndex >= cellCount) {
    throw new Error(
      `Plot group ${groupIndex + 1} has an invalid seed cell reference.`
    );
  }

  return cellIndex;
};

/**
 * Adapt plot properties to the normalized client representation.
 * This does not migrate backend data.
 */
const assignmentsFromLegacyTrial = (
  trial: SeedTrial
): SeedAssignmentsResponse => {
  if (!Array.isArray(trial.trial_plots)) {
    throw new Error("The trial response is missing its saved plot groups.");
  }

  const products = new Map<string, SavedSeedProductAssignment>();

  trial.trial_plots.forEach((group, groupIndex) => {
    const cellCount = getGridCellCount(group, groupIndex);
    const seeds = group.properties?.seeds;

    if (seeds === undefined || seeds === null) {
      return;
    }

    const entries = seeds.rates_and_dosages ?? [];

    if (!Array.isArray(entries)) {
      throw new Error(
        `Plot group ${groupIndex + 1} returned invalid seed assignments.`
      );
    }

    const seenCells = new Set<number>();

    entries.forEach((entry, entryIndex) => {
      const cellIndex = getLegacyCellIndex(
        entry,
        entryIndex,
        entries.length,
        cellCount,
        groupIndex
      );

      if (seenCells.has(cellIndex)) {
        throw new Error(
          `Plot group ${groupIndex + 1} has duplicate seed cell references.`
        );
      }

      seenCells.add(cellIndex);

      const productId = nonEmptyString(entry.product_id) ?? null;
      const variety = nonEmptyString(entry.variety);
      const hasRate = entry.rate !== undefined && entry.rate !== null;
      const hasProduct = productId !== null || variety !== undefined;

      if (!hasProduct) {
        if (hasRate && entry.rate !== 0) {
          throw new Error(
            `Plot group ${groupIndex + 1}, cell ${cellIndex + 1} ` +
              "has a seed rate but no product."
          );
        }

        return;
      }

      const rate = entry.rate;

      if (typeof rate !== "number" || !Number.isFinite(rate) || rate < 0) {
        throw new Error(
          `Plot group ${groupIndex + 1}, cell ${cellIndex + 1} ` +
            "has an invalid saved seed rate."
        );
      }

      const unit =
        nonEmptyString(entry.measure_unit) ??
        nonEmptyString(seeds.measure_unit);

      if (unit === undefined) {
        throw new Error(
          `Plot group ${groupIndex + 1} is missing its saved seed unit.`
        );
      }

      const isStandard = entry.is_standard === true;
      const productName = variety ?? productId;

      if (productName === null) {
        throw new Error("A saved seed product could not be identified.");
      }

      const key = JSON.stringify([
        productId,
        productName,
        normalizeSeedUnit(unit),
        isStandard,
      ]);

      let product = products.get(key);

      if (product === undefined) {
        product = {
          product_id: productId,
          variety: productName,
          measure_unit: unit,
          is_standard: isStandard,
          assignments: [],
        };

        products.set(key, product);
      }

      product.assignments.push({
        plot_group_id: groupIndex,
        plot_cell_id: cellIndex,
        rate,
        quantity: entry.quantity,
        color: nonEmptyString(entry.color),
      });
    });
  });

  return {
    seed_product_assignments: [...products.values()],
  };
};

/**
 * Prefer explicit normalized assignments, including an empty list.
 * Fall back to plot properties only when that field is absent.
 */
const getSeedAssignments = (trial: SeedTrial): SeedAssignmentsResponse => {
  const normalized = trial.plot_property_assignments;

  if (
    normalized !== undefined &&
    normalized !== null &&
    Object.prototype.hasOwnProperty.call(normalized, "seed_product_assignments")
  ) {
    if (
      normalized.seed_product_assignments !== null &&
      !Array.isArray(normalized.seed_product_assignments)
    ) {
      throw new Error(
        "The trial returned invalid normalized seed assignments."
      );
    }

    return normalized;
  }

  return assignmentsFromLegacyTrial(trial);
};

export const fetchSeedTrial = async (
  trialId: string,
  scope: AssignmentScope,
  signal?: AbortSignal
): Promise<SeedTrial> => {
  const response = await client.get<SeedTrial>(
    `/trial/${encodeURIComponent(trialId)}`,
    {
      params: {
        scope: "normalized_product_assignment",
      },
      headers: headersFor(scope),
      signal,
    }
  );

  const trial = response.data;

  return {
    ...trial,
    plot_property_assignments: getSeedAssignments(trial),
  };
};

/**
 * Fetch automatic seed assignments.
 *
 * Uses the configured API URL and current application credentials.
 * No trial ID, product ID, or bearer token is hardcoded.
 */
export const fetchSeedAutoAssignments = async (
  trialId: string,
  scope: AssignmentScope,
  signal?: AbortSignal
): Promise<SeedAutoAssignmentResponse> => {
  if (trialId.trim() === "") {
    throw new Error("A saved trial is required for automatic assignment.");
  }

  const response = await client.get<SeedAutoAssignmentResponse>(
    `/trial/${encodeURIComponent(trialId)}/auto-assign-plot-properties`,
    {
      params: {
        trial_type: "seed",
      },
      headers: headersFor(scope),
      signal,
    }
  );

  if (!Array.isArray(response.data?.trial_plots)) {
    throw new Error("The automatic assignment API returned invalid plot data.");
  }

  return response.data;
};

export const patchSeedAssignments = async (
  trialId: string,
  products: SavedSeedProductAssignment[],
  scope: AssignmentScope
): Promise<SeedAssignmentsResponse> => {
  if (
    products.length === 0 ||
    products.some((product) => product.assignments.length === 0)
  ) {
    throw new Error("Each saved product must have at least one plot.");
  }

  const seenCells = new Set<string>();

  products.forEach((product) => {
    product.assignments.forEach((assignment) => {
      if (
        !Number.isInteger(assignment.plot_group_id) ||
        assignment.plot_group_id < 0 ||
        !Number.isInteger(assignment.plot_cell_id) ||
        assignment.plot_cell_id < 0 ||
        !Number.isFinite(assignment.rate) ||
        assignment.rate < 0
      ) {
        throw new Error("A saved assignment has invalid cell data.");
      }

      const key = cellKey(assignment.plot_group_id, assignment.plot_cell_id);

      if (seenCells.has(key)) {
        throw new Error("A plot cell appears more than once in this save.");
      }

      seenCells.add(key);
    });
  });

  const response = await client.patch<SeedAssignmentsResponse>(
    `/trial/${encodeURIComponent(trialId)}/plot-property-assignments`,
    {
      seed_product_assignments: products,
    },
    {
      headers: headersFor(scope),
    }
  );

  if (!Array.isArray(response.data.seed_product_assignments)) {
    throw new Error(
      "The save API returned an invalid seed-assignment response."
    );
  }

  return response.data;
};

/**
 * Reset only the clicked product.
 * Normal Save validation remains in patchSeedAssignments.
 */
export const resetSeedAssignments = async (
  trial: SeedTrial,
  productId: string,
  scope: AssignmentScope
): Promise<SeedAssignmentsResponse> => {
  const savedProducts =
    getSeedAssignments(trial).seed_product_assignments ?? [];

  const matchingProducts = savedProducts.filter(
    (product) => product.product_id === productId
  );

  if (matchingProducts.length === 0) {
    throw new Error("There are no saved assignments for this seed product.");
  }

  if (matchingProducts.some((product) => product.is_standard)) {
    throw new Error("Standard assignments cannot be reset in this editor.");
  }

  const applicableProducts = matchingProducts.filter(
    (product) => product.assignments.length > 0
  );

  if (applicableProducts.length === 0) {
    throw new Error("There are no saved assignments for this seed product.");
  }

  const resetProducts: SavedSeedProductAssignment[] = savedProducts.map(
    (product) =>
      product.product_id === productId && product.assignments.length > 0
        ? { ...product, assignments: [] }
        : {
            ...product,
            assignments: product.assignments.map((assignment) => ({
              ...assignment,
            })),
          }
  );

  const response = await client.patch<SeedAssignmentsResponse>(
    `/trial/${encodeURIComponent(trial.id)}/plot-property-assignments`,
    {
      seed_product_assignments: resetProducts,
    },
    {
      headers: headersFor(scope),
    }
  );

  // The caller records PATCH success before validating its response.
  return response.data;
};

/** Reset only the applicable saved products passed by the Seeds flow. */
export const resetAllSeedAssignments = async (
  trial: SeedTrial,
  productIds: string[],
  scope: AssignmentScope
): Promise<SeedAssignmentsResponse | null> => {
  const requestedIds = new Set(productIds);
  const savedProducts =
    getSeedAssignments(trial).seed_product_assignments ?? [];
  const resettableProducts = savedProducts.filter(
    (product) =>
      product.product_id !== null &&
      requestedIds.has(product.product_id) &&
      !product.is_standard &&
      product.assignments.length > 0
  );

  if (resettableProducts.length === 0) {
    return null;
  }

  const resettableIds = new Set(
    resettableProducts.map((product) => product.product_id)
  );
  const resetProducts: SavedSeedProductAssignment[] = savedProducts.map(
    (product) =>
      product.product_id !== null && resettableIds.has(product.product_id)
        ? { ...product, assignments: [] }
        : {
            ...product,
            assignments: product.assignments.map((assignment) => ({
              ...assignment,
            })),
          }
  );

  const response = await client.patch<SeedAssignmentsResponse>(
    `/trial/${encodeURIComponent(trial.id)}/plot-property-assignments`,
    {
      seed_product_assignments: resetProducts,
    },
    {
      headers: headersFor(scope),
    }
  );

  // The caller records PATCH success before validating its response.
  return response.data;
};

export const patchSeedStandard = async (
  trial: SeedTrial,
  standard: SeedStandard,
  scope: AssignmentScope
): Promise<void> => {
  if (
    !nonEmptyString(standard.standard_seed_product_id) ||
    !nonEmptyString(standard.standard_product) ||
    !Number.isFinite(standard.standard_seed_rate) ||
    standard.standard_seed_rate <= 0
  ) {
    throw new Error("Select a field standard product and enter a valid rate.");
  }

  await client.patch(
    `/trial/${encodeURIComponent(trial.id)}`,
    {
      standard_field_properties: {
        ...trial.standard_field_properties,
        ...standard,
      },
    },
    {
      headers: headersFor(scope),
    }
  );
};

export const hydrateSeedRows = (
  trial: SeedTrial,
  design: SeedPlot[],
  products: SeedProduct[],
  unit: string
): SeedAssignmentRow[] => {
  if (trial.strip_design?.is_strip === true) {
    throw new Error(
      "This Seeds integration supports plot trials, not strip trials."
    );
  }

  const storedGroups = trial.trial_plots ?? [];

  if (storedGroups.length !== design.length) {
    throw new Error(
      "The displayed plot design does not match the saved trial. " +
        "Reload after saving Plot Design."
    );
  }

  const rows: SeedAssignmentRow[] = [];
  const seenFrontendIds = new Set<number>();

  storedGroups.forEach((stored, groupIndex) => {
    const cellCount = getGridCellCount(stored, groupIndex);
    const plot = stored.plot;
    const displayed = design[groupIndex]?.plot;

    if (plot === undefined || displayed === undefined) {
      throw new Error(`Plot group ${groupIndex + 1} is missing its design.`);
    }

    if (displayed.is_strip === true || displayed.isStripPlot === true) {
      throw new Error("Strip plots must use the separate stripe workflow.");
    }

    const groupId = displayed.id;

    if (
      typeof groupId !== "number" ||
      !Number.isFinite(groupId) ||
      seenFrontendIds.has(groupId)
    ) {
      throw new Error(
        `Plot group ${groupIndex + 1} has not finished frontend hydration ` +
          "or has a duplicate frontend ID."
      );
    }

    seenFrontendIds.add(groupId);

    const rowsCount = plot.rows ?? 0;
    const columns = plot.columns ?? 0;

    if (displayed.rows !== rowsCount || displayed.columns !== columns) {
      throw new Error(
        `Plot group ${groupIndex + 1} has different saved and displayed ` +
          `dimensions: saved ${rowsCount} × ${columns}, displayed ` +
          `${displayed.rows ?? "missing"} × ` +
          `${displayed.columns ?? "missing"}.`
      );
    }

    for (let cellIndex = 0; cellIndex < cellCount; cellIndex += 1) {
      rows.push({
        id:
          `${groupId}-${Math.floor(cellIndex / columns)}` +
          `-${cellIndex % columns}`,
        group: groupLabel(groupIndex),
        plot: cellIndex + 1,
        plotGroupId: groupIndex,
        plotCellId: cellIndex,
        productId: "",
        variety: "",
        seedRate: null,
        isStandard: false,
        color: "",
        legacy: false,
      });
    }
  });

  const byCell = new Map(
    rows.map((row) => [cellKey(row.plotGroupId, row.plotCellId), row])
  );

  const seenCells = new Set<string>();
  const normalized = getSeedAssignments(trial);

  (normalized.seed_product_assignments ?? []).forEach((saved) => {
    const productId = nonEmptyString(saved.product_id);
    const matches = products.filter(
      (product) => product.name === saved.variety
    );

    const resolvedId =
      productId ??
      (matches.length === 1
        ? matches[0].id
        : `legacy:${saved.measure_unit}:${saved.variety}`);

    saved.assignments.forEach((assignment) => {
      if (
        !Number.isInteger(assignment.plot_group_id) ||
        !Number.isInteger(assignment.plot_cell_id) ||
        assignment.plot_group_id < 0 ||
        assignment.plot_cell_id < 0
      ) {
        throw new Error("The API returned invalid plot-cell references.");
      }

      const key = cellKey(assignment.plot_group_id, assignment.plot_cell_id);
      const row = byCell.get(key);

      if (row === undefined || seenCells.has(key)) {
        throw new Error(
          "The API returned missing or duplicate plot-cell references."
        );
      }

      if (!Number.isFinite(assignment.rate) || assignment.rate < 0) {
        throw new Error("The API returned an invalid seed rate.");
      }

      seenCells.add(key);

      row.productId = resolvedId;
      row.variety = saved.variety;
      row.seedRate = convertSeedRate(assignment.rate, saved.measure_unit, unit);
      row.isStandard = saved.is_standard;
      row.color = assignment.color ?? "";
      row.legacy = productId === undefined;
    });
  });

  return rows;
};

/**
 * Map the auto-assignment response without replacing saved trial state.
 *
 * Clearing plot_property_assignments is intentional: otherwise the existing
 * saved normalized assignments take precedence over the generated properties.
 */
export const hydrateSeedAutoAssignmentRows = (
  trial: SeedTrial,
  response: SeedAutoAssignmentResponse,
  design: SeedPlot[],
  products: SeedProduct[],
  unit: string
): SeedAssignmentRow[] => {
  if (response.trial_plots.length === 0) {
    throw new Error("No plots were returned for automatic product assignment.");
  }

  const previewTrial: SeedTrial = {
    ...trial,
    trial_plots: response.trial_plots,
    plot_property_assignments: undefined,
  };

  const rows = hydrateSeedRows(previewTrial, design, products, unit);

  if (rows.length === 0) {
    throw new Error("No plot cells are available for automatic assignment.");
  }

  const hydratedRows = rows.map((row) => {
    const product = products.find((item) => item.id === row.productId);

    return {
      ...row,
      color: nonEmptyString(row.color) ?? nonEmptyString(product?.color) ?? "",
    };
  });

  if (
    hydratedRows.some((row) => {
      const product = products.find((item) => item.id === row.productId);

      return (
        row.productId === "" ||
        row.variety.trim() === "" ||
        row.seedRate === null ||
        !Number.isFinite(row.seedRate) ||
        row.seedRate < 0 ||
        row.legacy ||
        (product === undefined && !row.isStandard)
      );
    })
  ) {
    throw new Error(
      "Generated assignments must cover every plot cell with a valid product and rate."
    );
  }

  return hydratedRows;
};

const quantityForCell = (
  trial: SeedTrial,
  row: SeedAssignmentRow,
  unit: string
): number => {
  const rate = row.seedRate;
  const groups = trial.trial_plots ?? [];
  const group = groups[row.plotGroupId];

  if (rate === null || !Number.isFinite(rate) || rate < 0) {
    throw new Error("Every assigned cell must have a valid seed rate.");
  }

  if (
    !Number.isInteger(row.plotGroupId) ||
    row.plotGroupId < 0 ||
    !Number.isInteger(row.plotCellId) ||
    row.plotCellId < 0 ||
    group === undefined
  ) {
    throw new Error("A selected plot no longer exists in the saved design.");
  }

  const cellCount = getGridCellCount(group, row.plotGroupId);

  if (row.plotCellId >= cellCount) {
    throw new Error("A selected plot no longer exists in the saved design.");
  }

  const areaHa = calculatePlotAreaHa(
    row.plotCellId + 1,
    groups,
    undefined,
    row.plotGroupId
  );

  if (!Number.isFinite(areaHa) || areaHa <= 0) {
    throw new Error(
      "A selected plot has invalid dimensions. " +
        "Save its width and height in Plot Design before assigning seeds."
    );
  }

  const normalizedUnit = normalizeSeedUnit(unit);
  const rawQuantity = calculateTotalAmount(rate, areaHa, normalizedUnit);

  const quantity =
    normalizedUnit.includes("seeds") && !normalizedUnit.includes("kg")
      ? Math.round(rawQuantity)
      : rawQuantity;

  if (!Number.isFinite(quantity) || quantity < 0) {
    throw new Error("The calculated seed quantity is invalid.");
  }

  return quantity;
};

const getReplicantMultiplier = (group: SeedPlot | undefined): number => {
  const replicants = group?.replicants;

  if (Array.isArray(replicants)) {
    return 1 + replicants.length;
  }

  if (replicants !== null && typeof replicants === "object") {
    return 1 + Object.keys(replicants).length;
  }

  return 1;
};

const quantityMeasureUnit = (unit: string): string => {
  const normalized = normalizeSeedUnit(unit);

  if (normalized.includes("seeds") && !normalized.includes("kg")) {
    return "seeds";
  }

  return normalized.split("/")[0];
};

export const buildSeedSavePayload = (
  trial: SeedTrial,
  before: SeedAssignmentRow[],
  after: SeedAssignmentRow[],
  products: SeedProduct[],
  unit: string,
  explicitlySaved: string[],
  options: { replaceAllAssignments?: boolean } = {}
): SavedSeedProductAssignment[] => {
  const replaceAllAssignments = options.replaceAllAssignments === true;
  const affected = new Set(
    replaceAllAssignments
      ? after
          .map((row) => row.productId)
          .filter((productId) => productId !== "")
      : [
          ...explicitlySaved,
          ...after
            .filter(
              (row) => row.productId !== "" && !row.isStandard && !row.legacy
            )
            .map((row) => row.productId),
        ]
  );
  const beforeById = new Map(before.map((row) => [row.id, row]));
  const afterById = new Map(after.map((row) => [row.id, row]));

  const savedProducts =
    getSeedAssignments(trial).seed_product_assignments ?? [];

  if (
    beforeById.size !== before.length ||
    before.length !== after.length ||
    afterById.size !== after.length ||
    after.some((row) => !beforeById.has(row.id))
  ) {
    throw new Error(
      "The assignment draft does not match the current plot design."
    );
  }

  if (
    replaceAllAssignments &&
    after.some((row) => row.productId === "" || row.legacy)
  ) {
    throw new Error(
      "Every generated plot must have a recognized seed product before saving."
    );
  }

  after.forEach((row) => {
    const previous = beforeById.get(row.id);

    if (previous === undefined) {
      return;
    }

    if (
      previous.plotGroupId !== row.plotGroupId ||
      previous.plotCellId !== row.plotCellId
    ) {
      throw new Error("A draft changed the identity of a plot cell.");
    }

    const changed =
      previous.productId !== row.productId ||
      previous.seedRate !== row.seedRate ||
      previous.color !== row.color ||
      previous.isStandard !== row.isStandard;

    if (!changed) {
      return;
    }

    if (previous.legacy && !replaceAllAssignments) {
      throw new Error(
        "This cell has a legacy assignment without product_id. " +
          "Backend migration is required before editing it safely."
      );
    }

    if (previous.isStandard && !replaceAllAssignments) {
      throw new Error("Standard assignments cannot be changed in this editor.");
    }

    if (previous.productId !== "") {
      affected.add(previous.productId);
    }

    if (row.productId !== "") {
      affected.add(row.productId);
    }
  });

  const result: SavedSeedProductAssignment[] = replaceAllAssignments
    ? []
    : savedProducts
        .filter((product) => product.is_standard || product.product_id === null)
        .map((product) => ({
          ...product,
          assignments: product.assignments.map((assignment) => ({
            ...assignment,
          })),
        }));

  affected.forEach((productId) => {
    const owned = after.filter((row) => row.productId === productId);
    const previousOwned = before.filter((row) => row.productId === productId);

    if (owned.length === 0) {
      const movedToOtherProducts =
        previousOwned.length > 0 &&
        previousOwned.every((previous) => {
          const current = afterById.get(previous.id);

          return (
            current !== undefined &&
            current.productId !== "" &&
            current.productId !== productId
          );
        });

      if (movedToOtherProducts) {
        return;
      }

      throw new Error(
        "The API requires at least one assignment per saved product. " +
          "Removing a product's final plot without reassignment " +
          "requires backend clear support."
      );
    }

    if (
      owned.some((row) => row.legacy) ||
      (owned.some((row) => row.isStandard) && !replaceAllAssignments)
    ) {
      throw new Error("Legacy or standard assignments cannot be saved here.");
    }

    if (new Set(owned.map((row) => row.isStandard)).size > 1) {
      throw new Error(
        "A product cannot be both standard and non-standard in one save."
      );
    }

    const protocolProduct = products.find(
      (product) => product.id === productId
    );
    const savedProduct = savedProducts.find(
      (product) => product.product_id === productId
    );

    const variety =
      nonEmptyString(protocolProduct?.name) ??
      nonEmptyString(savedProduct?.variety);

    if (variety === undefined) {
      throw new Error("An affected seed product could not be identified.");
    }

    const totals = new Map<number, number>();

    const assignments = owned.map((row): SeedCellAssignment => {
      const rate = row.seedRate;

      if (rate === null) {
        throw new Error("A selected plot has no seed rate.");
      }

      const quantity = quantityForCell(trial, row, unit);
      const multiplier = getReplicantMultiplier(
        trial.trial_plots?.[row.plotGroupId]
      );

      totals.set(
        row.plotGroupId,
        (totals.get(row.plotGroupId) ?? 0) + quantity * multiplier
      );

      return {
        plot_group_id: row.plotGroupId,
        plot_cell_id: row.plotCellId,
        rate,
        quantity,
        color: row.color,
      };
    });

    result.push({
      product_id: productId,
      variety,
      measure_unit: unit,
      is_standard: owned[0].isStandard,
      assignments,
      plot_group_totals: [...totals.entries()].map(([plotGroupId, value]) => ({
        plot_group_id: plotGroupId,
        value,
        measure_unit: quantityMeasureUnit(unit),
      })),
    });
  });

  if (result.length === 0) {
    throw new Error("There are no assignments to save.");
  }

  return result;
};

export const seedApiError = (
  error: unknown
): {
  message: string;
  reloadRequired: boolean;
} => {
  if (!axios.isAxiosError(error)) {
    return {
      message:
        error instanceof Error ? error.message : "The seed operation failed.",
      reloadRequired: false,
    };
  }

  const status = error.response?.status;

  if (status === 409) {
    return {
      message:
        "The trial changed while saving. Reload before retrying. " +
        "Reloading discards the current draft.",
      reloadRequired: true,
    };
  }

  if (status === 404) {
    return {
      message:
        "The requested trial or seed endpoint was not found. " +
        "Verify the trial and backend assignment API support.",
      reloadRequired: true,
    };
  }

  if (status === 400 || status === 422) {
    return {
      message:
        "The API rejected the seed data. Check the field standard, rates, " +
        "units, plot references, and duplicate cells.",
      reloadRequired: false,
    };
  }

  return {
    message: "The request failed. Your unsaved draft has been retained.",
    reloadRequired: false,
  };
};
